/**
 * THE VERIFICATION QUEUE — two queues, one errand: what is waiting for me to
 * verify.
 *
 * PROVIDER DOCUMENTS, and since script 275, PEOPLE. The file is still named
 * ProviderVerification and that is deliberate rather than overlooked: the
 * SURFACE is `provider_verification` in lib/sections.js and in CardScreen's
 * map, so renaming the file alone would leave the file and the surface
 * disagreeing in a new way, and renaming the surface moves a screen a desk
 * already knows where to find. The name is imprecise; the comment is the
 * record, and this is it.
 *
 * WHY BOTH LIVE HERE. sections.js maps ONE PERMISSION TO ONE SURFACE, and
 * approve_verification already owns this one. The alternative was minting a
 * permission for the individual queue -- and the founder had just chosen reuse
 * over minting on the fica_review/fica_verify ghosts, for the same reason: one
 * act should not acquire two permissions.
 *
 * ---------------------------------------------------------------------
 * THE PROVIDER HALF — the admin half of "how does a provider get verified".
 *
 *   "how does a provider get verified? if it's something admin has to do, write
 *    the path for it in the Orchid Portal. otherwise, make clear to the provider
 *    exactly what they need to do ... let's not leave anyone in the dark."
 *
 * It is something admin has to do, and this is the path.
 *
 * WHAT WAS MISSING, AND IT WAS NOT A SCREEN
 *
 * The Verified badge is computed from four verified facts and has been since
 * script 09. Every one of them is a timestamp somebody has to write after
 * looking at a document — and there was nowhere to send a document, nowhere for
 * it to land, and nobody with a queue of them. The badge was derived from
 * evidence that could not be submitted.
 *
 * Script 73 built the other three quarters. This is the last quarter: the list,
 * the document, and the two buttons.
 *
 * A REJECTION MUST SAY WHY, AND THE DATABASE AGREES
 *
 * fn_review_provider_document refuses a rejection with a note shorter than a
 * sentence, and provider_document_submissions carries a CHECK constraint saying
 * the same thing. The reason is not paperwork: it is the text that appears on
 * the provider's own screen, and it is the only thing standing between them and
 * sending the identical document again next week.
 *
 * THE DOCUMENT IS OPENED THROUGH A SIGNED LINK, not rendered inline. It is
 * somebody's identity document; it should be looked at deliberately, and the
 * link expires in five minutes.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, Field, StatusPill, PrimaryCTA, QuietButton, Notice, LoadingPanel,
  textInputStyle,
} from '../ui';
import {
  fetchVerificationQueue, reviewProviderDocument, openProviderDocument, documentKind,
  identityFootprint, FOOTPRINT_KINDS, fetchKycQueue, decideAnIdentity,
  fetchTrustTaskQueue, verifyTrustTask,
} from '../lib/compliance';

const MIN_NOTE = 10;

// fn_log_my_admin_action refuses a reason under eight characters, and it runs
// BEFORE fn_decide_an_identity resolves the code -- so on the identity queue a
// reason is required on every decision, not only on a rejection. Said here so
// somebody is told before they press rather than by Postgres afterwards.
const MIN_REASON = 8;

function Decision({ item, onDone, onCancel }) {
  const [accept, setAccept] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const needsNote = accept === false;
  const tooShort = note.trim().length < MIN_NOTE;

  const send = async () => {
    setBusy(true);
    setError(null);
    const { data, error: err } = await reviewProviderDocument({
      submissionId: item.submission_id, accept, note,
    });
    setBusy(false);
    if (err) { setError(err); return; }
    onDone(data);
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${O.hairline}` }}>
      {error && <Notice tone="critical">{error}</Notice>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {[[true, 'It checks out'], [false, 'Send it back']].map(([value, label]) => {
          const on = accept === value;
          return (
            <button
              key={label}
              type="button"
              aria-pressed={on}
              onClick={() => setAccept(value)}
              style={{
                padding: '9px 15px', borderRadius: 999, cursor: 'pointer',
                background: on ? (value ? O.green : O.critical) : 'transparent',
                border: `1px solid ${on ? (value ? O.green : O.critical) : O.hairlineOn}`,
                color: on ? '#FFFFFF' : O.inkMuted,
                fontFamily: SANS, fontSize: 12.5, fontWeight: on ? 700 : 600,
              }}
            >{label}</button>
          );
        })}
      </div>

      <label
        htmlFor={`note-${item.submission_id}`}
        style={{ display: 'block', fontSize: 12, fontWeight: 700, color: O.ink, marginBottom: 6 }}
      >
        {needsNote ? 'What is wrong with it' : 'Note (optional)'}
      </label>
      <textarea
        id={`note-${item.submission_id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder={needsNote ? 'The ID photograph is cut off at the bottom edge.' : ''}
        style={{
          width: '100%', boxSizing: 'border-box', resize: 'vertical',
          padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13,
          border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
        }}
      />
      {needsNote && (
        <p style={{ marginTop: 6, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
          They read this on their own screen. It is the only thing that stops them sending
          the identical document again.
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <PrimaryCTA
          busy={busy}
          disabled={accept === null || (needsNote && tooShort)}
          onClick={send}
          style={{ flex: 2 }}
        >
          {busy ? 'Recording…' : 'Record this decision'}
        </PrimaryCTA>
        <div style={{ flex: 1 }}>
          <QuietButton onClick={onCancel}>Not now</QuietButton>
        </div>
      </div>
    </div>
  );
}

function Row({ item, onDecided }) {
  const [open, setOpen] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const kind = documentKind(item.kind);

  const view = async () => {
    setLinkError(null);
    const { data, error } = await openProviderDocument(item.storage_path);
    if (error) { setLinkError(error); return; }
    window.open(data, '_blank', 'noopener,noreferrer');
  };

  const claimed = [
    item.claimed_number && ['Number', item.claimed_number],
    item.claimed_amount && ['Cover', `R${Number(item.claimed_amount).toLocaleString('en-ZA')}`],
    item.claimed_expires && ['Runs to', item.claimed_expires],
    item.claimed_issuer && ['Issued by', item.claimed_issuer],
    item.category_slug && ['Trade', item.category_slug],
  ].filter(Boolean);

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
          {item.business_name}
        </h3>
        <StatusPill tone="info">{kind.label}</StatusPill>
        {item.waiting_days >= 3 && (
          <StatusPill tone="warning">Waiting {item.waiting_days} days</StatusPill>
        )}
      </div>

      <p style={{ marginTop: 6, fontSize: 11.5, color: O.inkFaint }}>
        {item.ofx_business_code || '—'} · {item.reference_number}
      </p>

      {/* WHO THIS DOCUMENT IS SUPPOSED TO BE, ON THE SAME SCREEN AS THE
          DOCUMENT.
            "on the provider's side, as well as on Orchid's, they should be able
             to see who the ultimate beneficial owner is - their OFX Code, and
             the linked OFXB code if it's a Sole Proprietor."
          A reviewer who has to go and look the name up somewhere else is a
          reviewer who approves an ID against nothing in particular. */}
      {item.owner_legal_name && (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(13,61,42,0.04)',
        }}>
          <p style={{ margin: 0, fontSize: 12, color: O.inkMuted }}>
            Beneficial owner{' '}
            <strong style={{ color: O.ink }}>{item.owner_legal_name}</strong>
            {item.owner_ofx_code ? (
              <span style={{ color: O.green, fontWeight: 700 }}> · {item.owner_ofx_code}</span>
            ) : null}
          </p>
          {/* What accepting this document will MEAN, which differs by form. */}
          <p style={{ margin: '5px 0 0', fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
            {item.business_form === 'sole_proprietor'
              ? 'A sole proprietorship is its owner — accepting this verifies the business itself.'
              : 'A company is not its owner. This verifies one half; the registration is the other.'}
          </p>
        </div>
      )}

      {kind.check && (
        <p style={{ marginTop: 9, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
          {kind.check}
        </p>
      )}

      {claimed.length > 0 && (
        <div style={{ marginTop: 11 }}>
          {claimed.map(([k, v]) => (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between', gap: 16,
              padding: '7px 0', borderBottom: `1px solid ${O.hairline}`,
            }}>
              {/* WHAT THEY SAID, not what is true. These are held apart from the
                  verified columns on purpose: they become facts about the
                  business only once somebody has looked at the document. */}
              <span style={{ fontSize: 12, color: O.inkMuted }}>{k}, as they typed it</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: O.ink, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {linkError && <div style={{ marginTop: 12 }}><Notice tone="critical">{linkError}</Notice></div>}

      {open ? (
        <Decision
          item={item}
          onDone={(row) => { setOpen(false); onDecided(row); }}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={view}
            style={{
              padding: '9px 15px', borderRadius: 999, background: 'transparent',
              border: `1px solid ${O.hairlineOn}`, color: O.green,
              fontFamily: SANS, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >Open the document</button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{
              padding: '9px 15px', borderRadius: 999, background: O.green,
              border: 'none', color: '#FFFFFF',
              fontFamily: SANS, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >Decide</button>
        </div>
      )}
    </Card>
  );
}

/**
 * ONE ID, SEEN WHOLE.
 *
 *   "a single ID can be tied to more than one provider profile ... I feel like
 *    there are too many doors, and too many opportunities for the same ID,
 *    unhashed, to do a lot of things at once. this must have a tight rein on it."
 *
 * The rein is in the database. This is the instrument that shows whether it is
 * holding: everything one identity is attached to, in one list, so "too many
 * doors" stops being a worry and becomes something somebody can look at.
 *
 * It sits behind view_kyc_document, which is the permission that opens this
 * screen — the same door as an identity document, because it answers the same
 * kind of question about the same person. The reason box is not decoration:
 * fn_log_my_admin_action refuses an unreasoned read, and the text is what
 * appears on the record of who looked somebody up and why.
 */
function IdentityFootprint() {
  const [idNumber, setIdNumber] = useState('');
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const look = async () => {
    setBusy(true);
    setFailure(null);
    const { data, error: err } = await identityFootprint({ lookup: idNumber, reason });
    setBusy(false);
    if (err) { setFailure(err); setRows(null); return; }
    setRows(data);
    // NOT KEPT ON SCREEN. The number was needed to ask the question and has no
    // business sitting in a text box afterwards, on a screen somebody may walk
    // away from.
    setIdNumber('');
  };

  return (
    <Card style={{ marginBottom: 18 }}>
      <h3 style={{ fontSize: 13.5, fontWeight: 700, color: O.ink, margin: 0 }}>
        Look up everything one person touches
      </h3>
      <p style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
        The person, businesses they own, businesses they are named in as a beneficial
        owner, field and office seats, a seat at Orchid, and anything already flagged.
        Either key finds them: an ID number, or the OFX Connect Code they read off
        their own account. An ID number is hashed on the way in — Orchid does not
        store it, and neither the number nor its hash comes back in the answer.
      </p>

      <div style={{ marginTop: 12 }}>
        {/* ONE FIELD, EITHER KEY. Not two inputs and a radio button: a Connect
            Code starts OFX- and an ID number does not, so the database can tell
            them apart and asking the reviewer to declare which one they hold is
            asking them to answer a question the answer is already inside. */}
        <Field label="ID number or OFX Connect Code" htmlFor="footprint-id"
               hint="A Connect Code looks like OFX-XXXXXXXXX.">
          <input id="footprint-id" type="text" autoComplete="off"
                 value={idNumber} onChange={(e) => setIdNumber(e.target.value)}
                 style={textInputStyle(false)} />
        </Field>
        <Field label="Why are you looking this person up?" htmlFor="footprint-reason"
               hint="Recorded against your name. At least eight characters — the database refuses an unreasoned lookup.">
          <input id="footprint-reason" type="text" value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 style={textInputStyle(false)} />
        </Field>
      </div>

      {failure && <Notice tone="critical">{failure}</Notice>}

      <div style={{ marginTop: 12, maxWidth: 200 }}>
        <PrimaryCTA onClick={look} busy={busy}
                    disabled={busy || !idNumber.trim() || reason.trim().length < 8}>
          {busy ? 'Looking…' : 'Look up'}
        </PrimaryCTA>
      </div>

      {rows && rows.length === 0 && (
        <Notice tone="info">
          Orchid holds nothing against that. Nobody has recorded it on a profile,
          a business, a seat or a staff account.
        </Notice>
      )}

      {rows && rows.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {rows.map((r, i) => (
            <div key={`${r.kind}-${r.reference || i}`} style={{
              display: 'flex', flexWrap: 'wrap', gap: '2px 10px', alignItems: 'baseline',
              padding: '9px 0', borderTop: i === 0 ? 'none' : `1px solid ${O.hairline}`,
            }}>
              <StatusPill tone={r.kind === 'conflict_flag' ? 'critical' : 'quiet'}>
                {FOOTPRINT_KINDS[r.kind] || r.kind}
              </StatusPill>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: O.ink }}>{r.label}</span>
              {r.reference && (
                <code style={{ fontSize: 11.5, color: O.inkMuted }}>{r.reference}</code>
              )}
              <span style={{ fontSize: 11.5, color: O.inkMuted }}>{r.detail}</span>
              {r.status && (
                <span style={{ fontSize: 11.5, color: O.inkFaint }}>· {r.status}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * THE PEOPLE WAITING, which is a different queue from the one above and was
 * missing entirely until script 275.
 *
 * The queue above lists PROVIDER DOCUMENT SUBMISSIONS -- businesses, and the
 * papers a business files. This lists INDIVIDUALS: a landlord or a tenant who
 * has handed in an ID and a proof of address and is waiting on Orchid.
 *
 * WHY IT MATTERS MORE THAN IT LOOKS. A landlord cannot add their first unit
 * until their identity is VERIFIED -- eligibility derived from verified
 * compliance, never declared. Before 275 the only route to a verified identity
 * ran through a provider document submission, so a landlord who is not a
 * provider had no route at all and no screen showed that they were waiting.
 *
 * NO NAMES ON SCREEN. A Connect Code, the room they are in, what is on file and
 * how long they have waited. Opening the documents themselves is a separate act
 * under view_kyc_document, on the Documents section, and it logs.
 */
function Waiting({ item, onDecided }) {
  const [open, setOpen] = useState(false);
  const [accept, setAccept] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const short = reason.trim().length < MIN_REASON;
  const tooShortToReject = accept === false && reason.trim().length < MIN_NOTE;

  const send = async () => {
    setBusy(true); setError(null);
    const { data, error: err } = await decideAnIdentity({
      code: item.connect_code, accept, reason,
    });
    setBusy(false);
    if (err) { setError(err); return; }
    onDecided(data);
  };

  const held = [
    ['ID document', item.has_id_document],
    ['Proof of address', item.has_proof_of_address],
    // Script 270. The address they TYPED, which is what the proof of address is
    // checked against -- a desk comparing a document to a claim needs the claim.
    ['Address typed out', item.has_typed_address],
  ];

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          fontFamily: SANS, fontSize: 13.5, fontWeight: 700, color: O.ink,
          fontVariantNumeric: 'tabular-nums',
        }}>{item.connect_code}</span>
        <StatusPill tone="quiet">{item.room}</StatusPill>
        <span style={{ fontSize: 12, color: O.inkMuted }}>
          {Number(item.completeness ?? 0)}% complete
        </span>
        <span style={{ fontSize: 11.5, color: O.inkFaint }}>
          waiting {item.waiting_days} day{item.waiting_days === 1 ? '' : 's'}
        </span>
      </div>

      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {held.map(([label, on]) => (
          <StatusPill key={label} tone={on ? 'green' : 'quiet'}>
            {on ? label : `No ${label.toLowerCase()}`}
          </StatusPill>
        ))}
      </div>

      {!open ? (
        <div style={{ marginTop: 12, maxWidth: 260 }}>
          <QuietButton onClick={() => setOpen(true)}>Decide this one</QuietButton>
        </div>
      ) : (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${O.hairline}` }}>
          {error && <Notice tone="critical">{error}</Notice>}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {[[true, 'Verified'], [false, 'Send it back']].map(([value, label]) => {
              const on = accept === value;
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setAccept(value)}
                  style={{
                    padding: '9px 15px', borderRadius: 999, cursor: 'pointer',
                    background: on ? (value ? O.green : O.critical) : 'transparent',
                    border: `1px solid ${on ? (value ? O.green : O.critical) : O.hairlineOn}`,
                    color: on ? '#FFFFFF' : O.inkMuted,
                    fontFamily: SANS, fontSize: 12.5, fontWeight: on ? 700 : 600,
                  }}
                >{label}</button>
              );
            })}
          </div>

          <label
            htmlFor={`reason-${item.connect_code}`}
            style={{ display: 'block', fontSize: 12, fontWeight: 700, color: O.ink, marginBottom: 6 }}
          >
            Why are you recording this decision?
          </label>
          <textarea
            id={`reason-${item.connect_code}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={accept === false
              ? 'The proof of address is dated eleven months ago.'
              : 'documents match the typed address'}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13,
              border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
            }}
          />
          <p style={{ marginTop: 6, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
            {accept === false
              ? 'They read this on their own screen. It is the only thing that stops them sending the identical document again.'
              : 'Written to the append-only activity log under your name, before the decision is recorded, and not editable afterwards.'}
          </p>

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <PrimaryCTA
              busy={busy}
              disabled={accept === null || short || tooShortToReject}
              onClick={send}
              style={{ flex: 2 }}
            >
              {busy ? 'Recording…' : 'Record this decision'}
            </PrimaryCTA>
            <div style={{ flex: 1 }}>
              <QuietButton onClick={() => { setOpen(false); setAccept(null); setReason(''); }}>
                Not now
              </QuietButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


/**
 * A TRUST TASK WAITING TO BE VERIFIED — the terminal step of the platform's
 * centre, and until script 290 there was nowhere to perform it.
 *
 * fn_verify_a_trust_task was reached by NOTHING in any of the three
 * repositories. A task could be created, assigned, accepted, started, advanced,
 * evidenced, sealed and submitted, and then it stopped there permanently.
 *
 * THERE IS NO "SEND IT BACK" HERE, and that is the database's shape rather than
 * an omission. fn_verify_a_trust_task only verifies; the canon's Part Three
 * table gives a submitted task one forward door, and inventing a rejection in
 * this screen would be a transition nobody ruled.
 *
 * A ROW THAT CANNOT BE VERIFIED STILL APPEARS, and says why. The queue shows
 * every submitted task on purpose: a task in `submitted` with no integrity seal
 * did not come through the submit path -- which writes the seal and the state
 * in the same UPDATE -- so it means something wrote the table directly, and the
 * only people who can investigate that should see it rather than be protected
 * from it. The offer is withheld; the row is not.
 *
 * AND THE SCREEN DOES NOT RE-DERIVE WHETHER IT CAN BE VERIFIED. can_be_verified
 * comes from the database beside the rule it encodes. Recomputing the seal test
 * in JavaScript would be a second implementation of a rule ruled once, and the
 * two drift the first time either moves.
 */
function TrustTask({ item, onVerified }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const short = reason.trim().length < MIN_NOTE;

  const send = async () => {
    setBusy(true); setError(null);
    const { data, error: err } = await verifyTrustTask({ taskId: item.task_id, reason });
    setBusy(false);
    if (err) { setError(err); return; }
    onVerified(data);
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          fontFamily: SANS, fontSize: 13.5, fontWeight: 700, color: O.ink,
        }}>{item.property_reference || 'Unreferenced unit'}</span>
        <StatusPill tone="quiet">{item.task_type}</StatusPill>
        {item.channel && <StatusPill tone="quiet">{item.channel}</StatusPill>}
        <span style={{ fontSize: 11.5, color: O.inkFaint }}>
          waiting {item.days_waiting} day{item.days_waiting === 1 ? '' : 's'}
        </span>
      </div>

      {item.task_purpose && (
        <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, lineHeight: 1.6, color: O.inkMuted }}>
          {item.task_purpose}
        </p>
      )}

      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <StatusPill tone={item.evidence_complete ? 'green' : 'quiet'}>
          {item.evidence_complete ? 'Evidence complete' : 'Evidence incomplete'}
        </StatusPill>
        <StatusPill tone={item.geofence_confirmed ? 'green' : 'quiet'}>
          {item.geofence_confirmed ? 'Location confirmed' : 'No location'}
        </StatusPill>
      </div>

      {!item.can_be_verified ? (
        /* The reason comes from the database, whole. A screen that paraphrases
           it would be explaining a rule it does not enforce. */
        <div style={{ marginTop: 12 }}>
          <Notice tone="warning">{item.why_not}</Notice>
        </div>
      ) : !open ? (
        <div style={{ marginTop: 12, maxWidth: 260 }}>
          <QuietButton onClick={() => setOpen(true)}>Verify this one</QuietButton>
        </div>
      ) : (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${O.hairline}` }}>
          {error && <Notice tone="critical">{error}</Notice>}

          <label
            htmlFor={`task-reason-${item.task_id}`}
            style={{ display: 'block', fontSize: 12, fontWeight: 700, color: O.ink, marginBottom: 6 }}
          >
            What did you check?
          </label>
          <textarea
            id={`task-reason-${item.task_id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Photographs match the stand and the meter reading is legible."
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13,
              border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
            }}
          />
          <p style={{ marginTop: 6, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
            Written to the append-only activity log under your name, before the task is
            resolved, and not editable afterwards.
          </p>

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <PrimaryCTA busy={busy} disabled={short} onClick={send} style={{ flex: 2 }}>
              {busy ? 'Recording…' : 'Record this verification'}
            </PrimaryCTA>
            <div style={{ flex: 1 }}>
              <QuietButton onClick={() => { setOpen(false); setReason(''); }}>
                Not now
              </QuietButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ProviderVerification() {
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(null);
  const [said, setSaid] = useState(null);

  // THE SECOND QUEUE KEEPS ITS OWN ERROR. A desk may hold approve_verification
  // and not view_kyc_queue, and one refusal must not blank the other list --
  // that would read as "nothing is waiting", which is the most expensive
  // possible lie on a compliance screen.
  const [people, setPeople] = useState(null);
  const [peopleError, setPeopleError] = useState(null);

  // AND THE THIRD KEEPS ITS OWN TOO, for the reason the comment above gives:
  // script 290 may not be on this database yet, and that must not blank the two
  // queues that are.
  const [tasks, setTasks] = useState(null);
  const [tasksError, setTasksError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: err } = await fetchVerificationQueue();
    if (err) { setError(err); setQueue([]); } else { setError(null); setQueue(data); }

    const { data: waiting, error: wErr } = await fetchKycQueue();
    if (wErr) { setPeopleError(wErr); setPeople([]); } else {
      setPeopleError(null);
      setPeople(waiting);
    }

    // THE KYC BRANCH NO LONGER RETURNS EARLY. Its `return` was harmless while
    // it was the last thing in the function; adding a queue after it would have
    // made a desk without view_kyc_queue silently see no trust tasks either --
    // which is the "nothing is waiting" lie the comment above calls the most
    // expensive one possible on a compliance screen.
    const { data: submitted, error: tErr } = await fetchTrustTaskQueue();
    if (tErr) { setTasksError(tErr); setTasks([]); return; }
    setTasksError(null);
    setTasks(submitted);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section>
      <h2 style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: O.inkFaint, marginBottom: 12,
      }}>Provider documents waiting to be verified</h2>

      {/* Above the queue, because it answers a question somebody arrives with
          rather than one the queue raises. */}
      <IdentityFootprint />

      {error && <Notice tone="critical">{error}</Notice>}
      {said && <Notice tone={said.outcome === 'accepted' ? 'green' : 'warning'}>{said.message}</Notice>}

      {queue === null && !error && <LoadingPanel message="Reading the queue…" />}

      {queue && queue.length === 0 && !error && (
        <Card>
          <p style={{ fontSize: 13, lineHeight: 1.65, color: O.inkMuted, margin: 0 }}>
            Nothing is waiting. Providers send identity documents, company registrations,
            insurance schedules and trade certificates from their own Profile screen; each one
            lands here, oldest first, and accepting it is what turns their Verified badge on.
          </p>
        </Card>
      )}

      {(queue || []).map((item) => (
        <Row
          key={item.submission_id}
          item={item}
          onDecided={(row) => { setSaid(row); load(); }}
        />
      ))}

      <h2 style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: O.inkFaint, margin: '26px 0 12px',
      }}>People waiting to be verified</h2>

      {peopleError && <Notice tone="critical">{peopleError}</Notice>}

      {people === null && !peopleError && <LoadingPanel message="Reading who is waiting…" />}

      {people && people.length === 0 && !peopleError && (
        <Card>
          <p style={{ fontSize: 13, lineHeight: 1.65, color: O.inkMuted, margin: 0 }}>
            Nobody is waiting. A landlord or tenant who hands in an ID and a proof of address
            lands here, oldest first — and until you verify them they cannot add a unit, because
            eligibility is derived from verified compliance rather than claimed.
          </p>
        </Card>
      )}

      {(people || []).map((item) => (
        <Waiting
          key={item.connect_code}
          item={item}
          onDecided={(row) => {
            setSaid({ outcome: row?.outcome === 'verified' ? 'accepted' : 'rejected',
                      message: row?.outcome === 'verified'
                        ? `${row.connect_code} is verified. They can add a unit now.`
                        : `${row?.connect_code} was sent back.` });
            load();
          }}
        />
      ))}

      <h2 style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: O.inkFaint, margin: '26px 0 12px',
      }}>Trust tasks waiting to be verified</h2>

      {tasksError && <Notice tone="critical">{tasksError}</Notice>}

      {tasks === null && !tasksError && <LoadingPanel message="Reading what is submitted…" />}

      {tasks && tasks.length === 0 && !tasksError && (
        <Card>
          <p style={{ fontSize: 13, lineHeight: 1.65, color: O.inkMuted, margin: 0 }}>
            No trust task is waiting. One reaches this queue when its evidence is
            complete and it has been submitted with an integrity seal — and verifying it
            is the last step, so nothing moves past here without a desk.
          </p>
        </Card>
      )}

      {(tasks || []).map((item) => (
        <TrustTask
          key={item.task_id}
          item={item}
          onVerified={(row) => {
            setSaid({
              outcome: 'accepted',
              message: `Verified. The Trust Event is recorded${
                row?.location_recorded ? ' with its location' : ''}.`,
            });
            load();
          }}
        />
      ))}
    </section>
  );
}
